/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {BannerEl} from 'common/components/banner';
import {BannerAction, BannerSize, BannerTheme, BannerType} from 'common/components/banner/types';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ButtonBarEl} from 'common/components/button-bar';
import {ConfirmEl} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {ModalEl, ModalType} from 'common/components/modal';
import {SectionEl} from 'common/components/section';
import {CreateEntityModalEl} from 'common/widgets/create-entity-modal';
import {InlineCohortsModalEl} from 'common/widgets/inline-cohorts-modal';
import {LoadEntityModalEl} from 'common/widgets/load-entity-modal';
import {UpsellModalEl} from 'common/widgets/upsell-modal';
import {EntityType} from 'common/report/util/saved-entities/types';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function PanelsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Panels, Modals & Dialogs'>
      <SubSection title='mp-section' autoDoc={true}>
        <p sel='.desc'>Provides a panel section and it is collapsible by default</p>

        <CodeTipEl sel='.example'>
          <SectionEl attrs={{'header-title': `Click to see what happens`}}>
            <div sel='.mp-section-content'>
              <div sel='.emoji'>🙋 This content can be collapsed into the title above.</div>
            </div>
          </SectionEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-modal' autoDoc={true}>
        <p sel='.desc'>
          Modals should be presented when the user needs to complete a task that the current interface does not support.
          For example: a button to invite teammates might present a modal including a search field and a list of names
          to choose from. These are specifically great for presenting new flows while preserving the state of content.
        </p>

        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary}} on={{click: () => designSystem.helpers.openModal(`modal`)}}>
            Open modal
          </ButtonEl>

          <ModalEl
            sel='.wide-modal'
            attrs={{closeable: true, open: designSystem.state.open.modal}}
            on={{change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`modal`, ev.detail.state)}}
          >
            <div>
              <div sel='.mp-modal-content'>
                <div sel='.mp-modal-title'>Easily track events</div>
                <div sel='.mp-modal-subtitle'>
                  Click on any element on your page (e.g. the buy button) to create an event and access all historical
                  data for that event.
                </div>
              </div>
              <ButtonBarEl
                sel='.mp-button-bar-primary'
                on={{click: () => designSystem.helpers.closeModal(`modal`)}}
                attrs={{'arrow-next': true}}
              >
                Create your first event
              </ButtonBarEl>
            </div>
          </ModalEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`largeModal`)}}
          >
            Open large modal
          </ButtonEl>

          <ModalEl
            sel='.modal'
            attrs={{
              closeable: true,
              'grow-to-content': true,
              open: designSystem.state.open.largeModal,
            }}
            on={{change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`largeModal`, ev.detail.state)}}
          >
            <div>
              <div sel='.mp-modal-large-content'>
                <div sel='.emoji'>🙋</div>
                <div sel='.mp-modal-text'>We can do it!</div>
              </div>
            </div>
          </ModalEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`drawerModal`)}}
          >
            Open drawer modal
          </ButtonEl>

          <ModalEl
            sel='.modal'
            attrs={{
              'modal-type': ModalType.RightDrawer,
              closeable: true,
              open: designSystem.state.open.drawerModal,
            }}
            on={{change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`drawerModal`, ev.detail.state)}}
          >
            <div>
              <div sel='.mp-modal-drawer-content'>
                <div sel='.emoji'>🙋</div>
                <div sel='.mp-modal-text'>We can do it!</div>
              </div>
            </div>
          </ModalEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary}} on={{click: () => designSystem.helpers.openModal(`popup`)}}>
            Open popup
          </ButtonEl>

          <ModalEl
            attrs={{'modal-type': ModalType.Popup, closeable: true, open: designSystem.state.open.popup}}
            on={{change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`popup`, ev.detail.state)}}
          >
            <div>
              <div sel='.mp-modal-content'>
                <div sel='.mp-modal-title'>Easily track events</div>
                <div sel='.mp-modal-subtitle'>
                  Click on any element on your page (e.g. the buy button) to create an event and access all historical
                  data for that event.
                </div>
              </div>
              <ButtonBarEl sel='.mp-button-bar-primary' on={{click: () => designSystem.helpers.closeModal(`popup`)}}>
                Create your first event
              </ButtonBarEl>
            </div>
          </ModalEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-inline-cohorts-modal' autoDoc={true}>
        <p sel='.desc'>A modal that wraps cohorts-query-builder to create modals from non-cohort reports</p>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`inline-cohorts`)}}
          >
            Create inline cohort
          </ButtonEl>
          <InlineCohortsModalEl
            attrs={{
              open: designSystem.state.open[`inline-cohorts`],
              type: `query`,
              'project-id': 3,
            }}
            on={{
              change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`inline-cohorts`, ev.detail.state),
              commitStagedCohort: () => designSystem.helpers.closeModal(`inline-cohorts`),
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-confirm' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`confirm`)}}
          >
            Open confirm
          </ButtonEl>

          <ConfirmEl
            attrs={{open: designSystem.state.open.confirm}}
            on={{
              change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`confirm`, ev.detail.state),
              submit: (ev) => console.warn(((ev as unknown) as CustomEvent).detail),
            }}
          >
            <div>
              <div sel='.mp-confirm-title'>Unsaved changes</div>
              <div sel='.mp-confirm-description'>
                If you navigate away from this page you will lose your changes. Would you like to continue?
              </div>
            </div>
          </ConfirmEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`confirmDelete`)}}
          >
            Open delete
          </ButtonEl>

          <ConfirmEl
            attrs={{
              id: `confirmDelete`,
              theme: THEME.DELETE,
              'confirm-button-label': `Delete it`,
              closeable: true,
              open: designSystem.state.open.confirmDelete,
            }}
            on={{
              change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`confirmDelete`, ev.detail.state),
              submit: (ev) => console.warn(((ev as unknown) as CustomEvent).detail),
            }}
          >
            <div>
              <div sel='.mp-confirm-title'>Delete Universe?</div>
              <div sel='.mp-confirm-description'>Are you sure you want to delete the universe? The answer is 42</div>
            </div>
          </ConfirmEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-create-entity-modal' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`create-entity-modal`)}}
          >
            Create a cohort
          </ButtonEl>
          <CreateEntityModalEl
            attrs={{
              'entity-type': EntityType.Cohort,
              open: designSystem.state.open[`create-entity-modal`],
              'modal-title': `Create your cohort`,
              'project-id': 3,
            }}
            on={{
              cancel: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`create-entity-modal`, ev),
              save: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`create-entity-modal`, ev),
            }}
            props={{
              existingEntityNames: [`test`],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-load-entity-modal' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`load-entity-modal`)}}
          >
            Load a cohort
          </ButtonEl>
          <LoadEntityModalEl
            attrs={{
              'empty-message': `no saved entities`,
              'entity-type': `cohort`,
              open: designSystem.state.open[`load-entity-modal`],
              'modal-title': `Load Entity Modal`,
            }}
            on={{
              close: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`load-entity-modal`, ev),
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-upsell-modal' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`mp-upsell-modal`)}}
          >
            Upgrade
          </ButtonEl>
          <UpsellModalEl
            attrs={{
              header: `Quickly learn how your experiments and their variants impact your metrics`,
              description: `Learn how your experiments and their variants affect your KPIs across any of your boards — all in one place, in seconds. Track changes in your metrics over time, and confirm the validity of the results with confidence ratings to see which experiments are worth continuing.`,
              'feature-gate-type': `experiment-reporting`,
              'learn-more-button-url': `https://example.org`,
              closeable: true,
              open: designSystem.state.open[`mp-upsell-modal`],
            }}
            on={{
              change: (ev: CustomEvent) => designSystem.helpers.handleModalChange(`mp-upsell-modal`, ev.detail.state),
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-banner' autoDoc={true}>
        <p sel='.desc'>For displaying a message with header text and description</p>

        <CodeTipEl sel='.example.block'>
          <BannerEl
            attrs={{
              theme: BannerTheme.Info,
              'header-text': `Medium Summary Banner`,
              'body-text': `Let's just say this, ideally we don't have a lot of text. But, if you really need a lot of text, it will look like this. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`,
              ctas: [
                {
                  text: `Learn More`,
                  actionName: BannerAction.None,
                },
                {
                  text: `Dismiss`,
                  actionName: BannerAction.Dismiss,
                },
              ],
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example.block'>
          <BannerEl
            attrs={{
              theme: BannerTheme.Success,
              size: BannerSize.Small,
              'body-text': `You did the thing! Use this if you only have a line of text! Small summary banner`,
              ctas: [
                {
                  text: `Dismiss`,
                  actionName: BannerAction.Dismiss,
                },
              ],
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example.block'>
          <div sel='.mp-panel.first-panel'>
            <BannerEl
              attrs={{
                theme: BannerTheme.Success,
                type: BannerType.Embedded,
                'header-text': `This is an example of an embedded banner!`,
                'body-text': `Use this if you want to put it inside of another thing like this panel. Here's how you would format it, [syntax](https://really-really-really-really-really-really-long-link-to-test-behavior-of-cta).`,
                ctas: [
                  {
                    text: `Dismiss`,
                    actionName: BannerAction.Dismiss,
                  },
                ],
              }}
            />
            <h2>Panel</h2>
          </div>
        </CodeTipEl>

        <CodeTipEl sel='.example.block'>
          <div sel='.mp-panel.first-panel'>
            <BannerEl
              attrs={{
                theme: BannerTheme.Warn,
                type: BannerType.Embedded,
                size: BannerSize.Small,
                'body-text': `Here is another example of an embedded banner! This one is smol with a smaller width.`,
              }}
              style={{
                width: `300px`,
              }}
            />
            <h2>Panel</h2>
          </div>
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
